/**
 * `MapView`: the pure (Babylon-free) state and math behind `MapCamera` and the inset cameras
 * (03 §5, §10; 09 §5). It implements the whole `MapCameraApi`; `MapCamera` adds only the Babylon
 * camera sync. Unit-tested in node.
 *
 * View = (center world, mpp, rotDeg, centerPx stage px, rect stage px), exactly v6 `View`:
 * screen = centerPx + R(rot)·(world − center) / mpp, screen y down.
 */
import type { MapCameraApi, Rect, ViewPreset } from '../api.ts';
import {
  DEFAULT_TILE_MPPS,
  MPP_MAX,
  MPP_MIN,
  crispLevels,
  s2w,
  w2s,
  type ViewLike,
} from './projection.ts';

/** Car positions for follow and click-to-follow (world metres). `false` = not on the map. */
export interface CarPositionSource {
  readonly count: number;
  position(car: number, out: number[]): boolean;
}

/** World-space AABB the pan clamp keeps the view inside (the tile extent). */
export interface WorldExtent {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export type ViewEvent = 'change' | 'idle';

/** Follow smoothing time constant (03 §5): the offset center − car decays as exp(−dt/0.25) (no steady-state lag). */
export const FOLLOW_TAU_S = 0.25;

const EPS_MPP = 1e-9;
const EPS_POS = 1e-6;

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

export class MapView implements MapCameraApi, ViewLike {
  // ViewLike (projection state)
  cx = 0;
  cy = 0;
  mpp = 1;
  cos = 1;
  sin = 0;
  cpx = 0;
  cpy = 0;

  private _rotDeg = 0;
  private _rect: Rect = { x: 0, y: 0, w: 1920, h: 1080 };
  private readonly _center: [number, number] = [0, 0];
  private _preset: ViewPreset | null = null;
  private _following: number | null = null;
  /** follow smoothing state: offset center − car, decayed with exp(−dt/τ) */
  private followInit = false;
  private offX = 0;
  private offY = 0;
  private readonly listeners: Record<ViewEvent, (() => void)[]> = { change: [], idle: [] };
  private readonly tmp: number[] = [0, 0];
  private readonly tmp2: number[] = [0, 0];

  /** device px per stage px (u, or f in fit mode): crisp levels are judged in device mpp. */
  k = 1;
  /** tile level mpps (device mpp) for crisp levels */
  tileMpps: readonly number[] = DEFAULT_TILE_MPPS;
  /** world extent for the pan clamp; null = no clamp */
  extent: WorldExtent | null = null;
  /**
   * Pan clamp when the viewport is wider than the extent on one axis (09 §12):
   * 'lock' = the view centre locks to the extent centre on that axis (the plan's rule);
   * 'contain' = the extent may slide inside the viewport but never leave it.
   */
  clampMode: 'lock' | 'contain' = 'lock';
  /** zoom-out clamp (m per stage px): `MPP_MAX`, raised for a portrait fitted view (the layout's `mppMax`) */
  mppMax = MPP_MAX;
  /** reference (unfitted) preset mpp; always a crisp level (03 §10) */
  refMpp = Number.NaN;
  carSource: CarPositionSource | null = null;

  // fly-to animation (wall clock)
  private fly = { active: false, t: 0, dur: 0, x0: 0, y0: 0, l0: 0, x1: 0, y1: 0, l1: 0 };
  private crispCache: number[] = [];
  private crispKey = '';

  constructor(rect?: Rect) {
    if (rect) this._rect = { ...rect };
  }

  // ───────────── MapCameraApi: read-only state ─────────────
  get center(): [number, number] {
    this._center[0] = this.cx;
    this._center[1] = this.cy;
    return this._center;
  }
  get rotDeg(): number {
    return this._rotDeg;
  }
  get rect(): Rect {
    return this._rect;
  }
  get presetMpp(): number {
    return this._preset ? this._preset.mpp : this.mpp;
  }
  get preset(): ViewPreset | null {
    return this._preset;
  }
  get following(): number | null {
    return this._following;
  }
  get flying(): boolean {
    return this.fly.active;
  }

  /** Crisp levels (stage mpp) for idle snapping: tile levels × {1,1.5,2,3} × k, preset mpps, clamp ends. */
  crispLevels(): readonly number[] {
    const key = `${String(this.k)}|${this.tileMpps.join(',')}|${String(this.refMpp)}|${String(this.presetMpp)}|${String(this.mppMax)}`;
    if (key !== this.crispKey) {
      this.crispKey = key;
      this.crispCache = crispLevels(this.tileMpps, this.k, [this.refMpp, this.presetMpp, MPP_MIN, this.mppMax]).filter(
        (v) => v >= MPP_MIN - 1e-9 && v <= this.mppMax + 1e-9,
      );
    }
    return this.crispCache;
  }

  // ───────────── events ─────────────
  on(ev: ViewEvent, fn: () => void): () => void {
    const arr = this.listeners[ev];
    arr.push(fn);
    return () => {
      const i = arr.indexOf(fn);
      if (i >= 0) arr.splice(i, 1);
    };
  }
  protected emit(ev: ViewEvent): void {
    const arr = this.listeners[ev];
    for (let i = 0; i < arr.length; i++) arr[i]?.();
  }
  /** Called by the controller when the camera has been idle for 150 ms (after the crisp snap). */
  notifyIdle(): void {
    this.emit('idle');
  }
  /** Incremented on every view change (layers recompute visibility when it moves). */
  version = 0;

  /** Hook for subclasses (Babylon sync) and the 'change' event. */
  protected changed(): void {
    this.version++;
    this.emit('change');
  }

  // ───────────── projection ─────────────
  worldToStage(x: number, y: number, out: Float32Array | number[]): void {
    w2s(this, x, y, out);
  }
  stageToWorld(px: number, py: number, out: Float32Array | number[]): void {
    s2w(this, px, py, out);
  }

  // ───────────── setters ─────────────
  setRect(r: Rect): void {
    if (r.x === this._rect.x && r.y === this._rect.y && r.w === this._rect.w && r.h === this._rect.h) return;
    this._rect = { x: r.x, y: r.y, w: r.w, h: r.h };
    this.changed();
  }

  setRotation(rotDeg: number): void {
    this._rotDeg = rotDeg;
    const r = (rotDeg * Math.PI) / 180;
    this.cos = Math.cos(r);
    this.sin = Math.sin(r);
  }

  /** Set the view; centerPx is kept. Not clamped (presets may sit outside the pan clamp). */
  setView(center: readonly [number, number], mpp: number, rotDeg?: number): void {
    this.setViewRaw(center[0], center[1], mpp, rotDeg);
  }

  /** Allocation-free `setView`. */
  setViewRaw(cx: number, cy: number, mpp: number, rotDeg?: number): void {
    if (rotDeg !== undefined && rotDeg !== this._rotDeg) this.setRotation(rotDeg);
    this.cx = cx;
    this.cy = cy;
    this.mpp = mpp;
    this.changed();
  }

  /** Set the stage px where `center` lands. */
  setCenterPx(px: number, py: number): void {
    this.followInit = false;
    this.cpx = px;
    this.cpy = py;
    this.changed();
  }

  /** Make `p` (a fitted preset) the current preset and jump to it (rect = p.mapRect). */
  setPreset(p: ViewPreset): void {
    this.followInit = false;
    this._preset = clonePreset(p);
    if (!Number.isFinite(this.refMpp)) this.refMpp = p.mpp;
    this.fly.active = false;
    this._rect = { ...p.mapRect };
    this.cpx = p.centerPx[0];
    this.cpy = p.centerPx[1];
    this.setRotation(p.rotDeg);
    this.cx = p.center[0];
    this.cy = p.center[1];
    this.mpp = p.mpp;
    this.changed();
  }

  /**
   * Resize-preserving relayout (09 §5): at the preset → the new fitted preset; otherwise keep the
   * world point at the old map-rect centre (now at the new map-rect centre) and the zoom relative
   * to the fitted preset.
   */
  applyLayout(p: ViewPreset): void {
    this.followInit = false;
    if (!this._preset || this.isAtPreset()) {
      this.setPreset(p);
      return;
    }
    const rel = this.relativeZoom();
    const r0 = this._rect;
    s2w(this, r0.x + r0.w / 2, r0.y + r0.h / 2, this.tmp);
    const wx = this.tmp[0] ?? 0;
    const wy = this.tmp[1] ?? 0;
    this._preset = clonePreset(p);
    this._rect = { ...p.mapRect };
    this.cpx = p.centerPx[0];
    this.cpy = p.centerPx[1];
    this.setRotation(p.rotDeg);
    this.mpp = this.clampMpp(rel * p.mpp);
    this.placeWorldAt(wx, wy, p.mapRect.x + p.mapRect.w / 2, p.mapRect.y + p.mapRect.h / 2);
    this.changed();
  }

  /** Deep links (04): world centre at the map-rect centre and zoom relative to the fitted preset. */
  setRelativeView(wx: number, wy: number, rel: number): void {
    this.fly.active = false;
    this.mpp = this.clampMpp(rel * this.presetMpp);
    const r = this._rect;
    this.placeWorldAt(wx, wy, r.x + r.w / 2, r.y + r.h / 2);
    this.changed();
  }

  /** World point at the map-rect centre (into out). */
  viewCentreWorld(out: number[]): void {
    const r = this._rect;
    s2w(this, r.x + r.w / 2, r.y + r.h / 2, out);
  }

  isAtPreset(): boolean {
    const p = this._preset;
    if (!p) return false;
    return (
      Math.abs(this.mpp - p.mpp) <= EPS_MPP * p.mpp &&
      Math.abs(this.cx - p.center[0]) <= EPS_POS &&
      Math.abs(this.cy - p.center[1]) <= EPS_POS &&
      Math.abs(this.cpx - p.centerPx[0]) <= EPS_POS &&
      Math.abs(this.cpy - p.centerPx[1]) <= EPS_POS &&
      Math.abs(this._rotDeg - p.rotDeg) <= 1e-9
    );
  }

  /** `m` clamped to [MPP_MIN, mppMax] */
  clampMpp(m: number): number {
    return m < MPP_MIN ? MPP_MIN : m > this.mppMax ? this.mppMax : m;
  }

  relativeZoom(): number {
    return this.mpp / this.presetMpp;
  }

  // ───────────── interaction primitives ─────────────
  /** Zoom by `factor` (> 1 zooms in) keeping the world point under (px, py) fixed. Clamped to 0.25–`mppMax`. */
  zoomAt(px: number, py: number, factor: number): void {
    const next = this.clampMpp(this.mpp / factor);
    if (next === this.mpp) return;
    this.zoomToAt(px, py, next);
  }

  /** Set mpp keeping the world point under (px, py) fixed (no clamp). */
  zoomToAt(px: number, py: number, mpp: number): void {
    this.zoomToAtRaw(px, py, mpp);
    this.changed();
  }

  private zoomToAtRaw(px: number, py: number, mpp: number): void {
    s2w(this, px, py, this.tmp);
    const wx = this.tmp[0] ?? 0;
    const wy = this.tmp[1] ?? 0;
    this.mpp = mpp;
    this.placeWorldAt(wx, wy, px, py);
  }

  /** Move the picture by (dx, dy) stage px (drag semantics: content follows the pointer). Unclamped. */
  panBy(dxStage: number, dyStage: number): void {
    if (dxStage === 0 && dyStage === 0) return;
    this.panRaw(dxStage, dyStage, 1);
    this.changed();
  }

  /** Move `center` by the world displacement of a (dx, dy) stage-px pan, scaled by t. */
  private panRaw(dx: number, dy: number, t: number): void {
    const rx = -dx * this.mpp * t;
    const ry = dy * this.mpp * t;
    this.cx += rx * this.cos + ry * this.sin;
    this.cy += -rx * this.sin + ry * this.cos;
  }

  /**
   * Clamped pan (03 §5, 09 §12): the screen x and y components are applied as two steps, each
   * clipped (Liang–Barsky) against the feasible box of the view-centre world point, so a drag along
   * one screen axis never slides the picture along the other. Returns a bitmask of the clipped
   * screen components (1 = x, 2 = y) so inertia can zero just those.
   */
  panByClamped(dxStage: number, dyStage: number): number {
    if (!this.extent) {
      this.panBy(dxStage, dyStage);
      return 0;
    }
    let clipped = 0;
    if (dxStage !== 0 && this.clipStep(dxStage, 0) < 1) clipped |= 1;
    if (dyStage !== 0 && this.clipStep(0, dyStage) < 1) clipped |= 2;
    if (dxStage !== 0 || dyStage !== 0) this.changed();
    return clipped;
  }

  /** One clipped pan step; returns the applied fraction t ∈ [0, 1]. */
  private clipStep(dx: number, dy: number): number {
    if (!this.feasibleBox(true)) {
      this.panRaw(dx, dy, 1);
      return 1;
    }
    const r = this._rect;
    s2w(this, r.x + r.w / 2, r.y + r.h / 2, this.tmp);
    const vx = this.tmp[0] ?? 0;
    const vy = this.tmp[1] ?? 0;
    // world displacement of the view centre for the full step
    const rx = -dx * this.mpp;
    const ry = dy * this.mpp;
    const wx = rx * this.cos + ry * this.sin;
    const wy = -rx * this.sin + ry * this.cos;
    const b = this.box;
    let t = 1;
    if (wx > 0) t = Math.min(t, (b.x1 - vx) / wx);
    else if (wx < 0) t = Math.min(t, (b.x0 - vx) / wx);
    if (wy > 0) t = Math.min(t, (b.y1 - vy) / wy);
    else if (wy < 0) t = Math.min(t, (b.y0 - vy) / wy);
    t = Math.max(0, Math.min(1, t));
    if (t > 0) this.panRaw(dx, dy, t);
    return t;
  }

  /** Shift `center` so that world (wx, wy) lands on stage (px, py), mpp and rotation unchanged. */
  private placeWorldAt(wx: number, wy: number, px: number, py: number): void {
    // s2w(p) with center = 0 gives the offset R⁻¹((p − cpx)·mpp); center = W − offset.
    const rx = (px - this.cpx) * this.mpp;
    const ry = -(py - this.cpy) * this.mpp;
    this.cx = wx - (rx * this.cos + ry * this.sin);
    this.cy = wy - (-rx * this.sin + ry * this.cos);
  }

  private readonly box = { x0: 0, y0: 0, x1: 0, y1: 0 };

  /**
   * Feasible box for the world point at the map-rect centre: the extent minus half the viewport
   * (the viewport's world AABB half-size). On an axis where the viewport is wider than the extent,
   * 'lock' = the extent centre, 'contain' = the extent stays inside the viewport. With `soft`, each
   * axis interval is widened to include the current position, so a view outside the box (a preset
   * on a locked axis) never jumps: it may only move towards the box.
   */
  private feasibleBox(soft: boolean): boolean {
    const e = this.extent;
    if (!e) return false;
    const r = this._rect;
    const ac = Math.abs(this.cos);
    const as = Math.abs(this.sin);
    const hx = (ac * r.w + as * r.h) * 0.5 * this.mpp;
    const hy = (as * r.w + ac * r.h) * 0.5 * this.mpp;
    const b = this.box;
    axisInterval(e.x0, e.x1, hx, this.clampMode, b, 'x');
    axisInterval(e.y0, e.y1, hy, this.clampMode, b, 'y');
    if (soft) {
      s2w(this, r.x + r.w / 2, r.y + r.h / 2, this.tmp);
      const vx = this.tmp[0] ?? 0;
      const vy = this.tmp[1] ?? 0;
      if (vx < b.x0) b.x0 = vx;
      if (vx > b.x1) b.x1 = vx;
      if (vy < b.y0) b.y0 = vy;
      if (vy > b.y1) b.y1 = vy;
    }
    return true;
  }

  /**
   * Pan clamp after a zoom or snap (03 §5, 09 §12): the view centre is moved into the feasible
   * box. Axes where the viewport is wider than the extent are clamped softly (never pushed: a
   * preset off the extent centre stays put). Returns true if it moved the view.
   */
  clampToExtent(): boolean {
    if (!this.clampRaw()) return false;
    this.changed();
    return true;
  }

  private clampRaw(): boolean {
    const e = this.extent;
    if (!e || !this.feasibleBox(false)) return false;
    const r = this._rect;
    s2w(this, r.x + r.w / 2, r.y + r.h / 2, this.tmp);
    const vx = this.tmp[0] ?? 0;
    const vy = this.tmp[1] ?? 0;
    const b = this.box;
    const ac = Math.abs(this.cos);
    const as = Math.abs(this.sin);
    const lockX = e.x1 - e.x0 <= (ac * r.w + as * r.h) * this.mpp;
    const lockY = e.y1 - e.y0 <= (as * r.w + ac * r.h) * this.mpp;
    let nx = vx;
    let ny = vy;
    if (!lockX) nx = vx < b.x0 ? b.x0 : vx > b.x1 ? b.x1 : vx;
    if (!lockY) ny = vy < b.y0 ? b.y0 : vy > b.y1 ? b.y1 : vy;
    if (nx === vx && ny === vy) return false;
    this.cx += nx - vx;
    this.cy += ny - vy;
    return true;
  }

  /**
   * Snap the translation to whole stage px (03 §5 pixel-art integrity): the preset centre (or the
   * world origin without a preset) lands on an integer offset from centerPx, so the preset itself
   * is a fixed point of the snap.
   */
  snapToStagePx(): void {
    if (this.snapRaw()) this.changed();
  }

  private snapRaw(): boolean {
    const ax = this._preset ? this._preset.center[0] : 0;
    const ay = this._preset ? this._preset.center[1] : 0;
    w2s(this, ax, ay, this.tmp);
    const ox = (this.tmp[0] ?? 0) - this.cpx;
    const oy = (this.tmp[1] ?? 0) - this.cpy;
    const dx = Math.round(ox) - ox;
    const dy = Math.round(oy) - oy;
    if (Math.abs(dx) < 1e-9 && Math.abs(dy) < 1e-9) return false;
    this.panRaw(dx, dy, 1);
    return true;
  }

  /**
   * End state of an idle snap (no events): zoom to `targetMpp` anchored at (ax, ay), clamp, and
   * (unless following) snap the translation. Writes [cx, cy, mpp] into `out`.
   */
  snapEndState(ax: number, ay: number, targetMpp: number, out: number[]): void {
    const cx = this.cx;
    const cy = this.cy;
    const mpp = this.mpp;
    if (targetMpp !== mpp) this.zoomToAtRaw(ax, ay, targetMpp);
    this.clampRaw();
    if (this._following === null) this.snapRaw();
    out[0] = this.cx;
    out[1] = this.cy;
    out[2] = this.mpp;
    this.cx = cx;
    this.cy = cy;
    this.mpp = mpp;
  }

  // ───────────── animation, follow ─────────────
  /** Wall-clock eased fly-to (0 s = jump). While following, only mpp animates. */
  flyTo(center: [number, number], mpp: number, durationSec: number): void {
    const target = this.clampMpp(mpp);
    if (durationSec <= 0) {
      this.fly.active = false;
      if (this._following === null) this.setViewRaw(center[0], center[1], target);
      else this.setViewRaw(this.cx, this.cy, target);
      return;
    }
    const f = this.fly;
    f.active = true;
    f.t = 0;
    f.dur = durationSec;
    f.x0 = this.cx;
    f.y0 = this.cy;
    f.l0 = Math.log(this.mpp);
    f.x1 = center[0];
    f.y1 = center[1];
    f.l1 = Math.log(target);
  }

  /** Fly to a preset (also restores its centerPx, rect and rotation immediately). */
  flyToPreset(p: ViewPreset, durationSec: number): void {
    this.followInit = false;
    this._preset = clonePreset(p);
    if (!Number.isFinite(this.refMpp)) this.refMpp = p.mpp;
    if (durationSec <= 0) {
      this.setPreset(p);
      return;
    }
    // keep the picture continuous: re-express the current view with the new centerPx/rect
    s2w(this, p.centerPx[0], p.centerPx[1], this.tmp);
    this._rect = { ...p.mapRect };
    this.cpx = p.centerPx[0];
    this.cpy = p.centerPx[1];
    this.cx = this.tmp[0] ?? this.cx;
    this.cy = this.tmp[1] ?? this.cy;
    this.setRotation(p.rotDeg);
    this.changed();
    this.flyTo([p.center[0], p.center[1]], p.mpp, durationSec);
  }

  cancelFly(): void {
    this.fly.active = false;
  }

  follow(car: number | null): void {
    this._following = car;
    this.followInit = false;
  }

  /** Back to the current (fitted) preset; animated when `durationSec` > 0. */
  reset(durationSec = 0): void {
    const p = this._preset;
    if (!p) return;
    if (durationSec > 0) this.flyToPreset(p, durationSec);
    else this.setPreset(p);
  }

  /**
   * Per-frame update with wall-clock dt (UI time, 05 determinism exception): fly-to and follow.
   * Allocation-free.
   */
  update(dtWall: number): void {
    const f = this.fly;
    if (f.active) {
      f.t += dtWall;
      const a = Math.min(1, f.t / f.dur);
      const e = easeInOutCubic(a);
      const mpp = Math.exp(f.l0 + (f.l1 - f.l0) * e);
      if (this._following === null) {
        this.cx = f.x0 + (f.x1 - f.x0) * e;
        this.cy = f.y0 + (f.y1 - f.y0) * e;
      }
      this.mpp = a >= 1 ? Math.exp(f.l1) : mpp;
      if (a >= 1) {
        f.active = false;
        if (this._following === null) {
          this.cx = f.x1;
          this.cy = f.y1;
        }
        const p = this._preset;
        // land exactly on the preset when that was the target (isAtPreset after reset/tab switch)
        if (p && Math.abs(f.x1 - p.center[0]) < EPS_POS && Math.abs(f.y1 - p.center[1]) < EPS_POS && Math.abs(Math.exp(f.l1) - p.mpp) < 1e-9) {
          this.mpp = p.mpp;
        }
      }
      this.changed();
    }
    const car = this._following;
    if (car !== null && this.carSource && dtWall > 0) {
      if (this.carSource.position(car, this.tmp2)) {
        // smooth the offset, not the centre: zero steady-state lag behind a moving car
        const tx = this.tmp2[0] ?? this.cx;
        const ty = this.tmp2[1] ?? this.cy;
        if (!this.followInit) {
          this.offX = this.cx - tx;
          this.offY = this.cy - ty;
          this.followInit = true;
        }
        const k = Math.exp(-dtWall / FOLLOW_TAU_S);
        this.offX *= k;
        this.offY *= k;
        const nx = tx + this.offX;
        const ny = ty + this.offY;
        if (nx !== this.cx || ny !== this.cy) {
          this.cx = nx;
          this.cy = ny;
          this.changed();
        }
      }
    }
  }
}

function axisInterval(lo: number, hi: number, half: number, mode: 'lock' | 'contain', b: { x0: number; y0: number; x1: number; y1: number }, axis: 'x' | 'y'): void {
  let a: number;
  let c: number;
  if (hi - lo <= 2 * half) {
    if (mode === 'lock') {
      a = c = (lo + hi) / 2;
    } else {
      a = hi - half; // contain: the extent stays inside the viewport
      c = lo + half;
    }
  } else {
    a = lo + half;
    c = hi - half;
  }
  if (axis === 'x') {
    b.x0 = a;
    b.x1 = c;
  } else {
    b.y0 = a;
    b.y1 = c;
  }
}

function clonePreset(p: ViewPreset): ViewPreset {
  const out: ViewPreset = {
    center: [p.center[0], p.center[1]],
    mpp: p.mpp,
    rotDeg: p.rotDeg,
    centerPx: [p.centerPx[0], p.centerPx[1]],
    mapRect: { ...p.mapRect },
  };
  if (p.crisp !== undefined) out.crisp = p.crisp;
  if (p.frameRect) out.frameRect = { ...p.frameRect };
  return out;
}
